import React, { useState } from 'react';
import { VideoPlayer } from './video-player';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Play, User, Monitor } from 'lucide-react';

interface VideoOption {
  type: 'composite' | 'webcam' | 'screenshare';
  label: string;
  url: string;
  participant?: string | null;
}

interface MultiVideoPlayerProps {
  videos: {
    composite_video?: { url: string | null; type: string } | null;
    participant_videos?: Record<string, {
      participant_identity: string;
      webcam?: { url: string } | null;
      screenshare?: { url: string } | null;
    }>;
    video_options?: VideoOption[];
  };
  className?: string;
}

export function MultiVideoPlayer({ videos, className }: MultiVideoPlayerProps) {
  const [selectedVideo, setSelectedVideo] = useState<VideoOption | null>(null);

  // Initialize with the first available video
  React.useEffect(() => {
    if (!selectedVideo && videos.video_options && videos.video_options.length > 0) {
      setSelectedVideo(videos.video_options[0]);
    }
  }, [videos, selectedVideo]);

  if (!videos.video_options || videos.video_options.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        No videos available
      </div>
    );
  }

  const getVideoIcon = (type: string) => {
    switch (type) {
      case 'webcam':
        return <User className="h-4 w-4" />;
      case 'screenshare':
        return <Monitor className="h-4 w-4" />;
      default:
        return <Play className="h-4 w-4" />;
    }
  };

  const getVideoPreviewUrl = (url: string) => {
    // For now, we'll use a placeholder. In production, you'd generate actual thumbnails
    return url;
  };

  return (
    <div className={cn("space-y-4", className)}>
      {/* Main Video Player */}
      <div className="relative">
        {selectedVideo && (
          <VideoPlayer url={selectedVideo.url} className="w-full aspect-video" />
        )}
      </div>

      {/* Video Thumbnails */}
      {videos.video_options.length > 1 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-muted-foreground">Available Videos</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {videos.video_options.map((option, index) => (
              <button
                key={index}
                onClick={() => setSelectedVideo(option)}
                className={cn(
                  "relative group overflow-hidden rounded-lg border-2 transition-all",
                  selectedVideo?.url === option.url
                    ? "border-primary ring-2 ring-primary/20"
                    : "border-border hover:border-primary/50"
                )}
              >
                {/* Video Thumbnail */}
                <div className="relative aspect-video bg-muted">
                  <video
                    src={getVideoPreviewUrl(option.url)}
                    className="w-full h-full object-cover"
                    muted
                    preload="metadata"
                  />
                  
                  {/* Overlay */}
                  <div className={cn(
                    "absolute inset-0 bg-black/40 flex items-center justify-center transition-opacity",
                    selectedVideo?.url === option.url ? "opacity-0" : "group-hover:opacity-100"
                  )}>
                    <div className="bg-white/90 dark:bg-black/90 rounded-full p-2">
                      <Play className="h-6 w-6" />
                    </div>
                  </div>

                  {/* Video Type Badge */}
                  <div className="absolute top-2 left-2 bg-black/60 text-white px-2 py-1 rounded text-xs flex items-center gap-1">
                    {getVideoIcon(option.type)}
                    <span className="capitalize">{option.type}</span>
                  </div>
                </div>

                {/* Label */}
                <div className="p-2 bg-background">
                  <p className="text-xs font-medium truncate">
                    {option.label}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}